import React from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import config from '../../config';
import { getListingsById } from '../../ducks/marketplaceData.duck';
import { isScrollingDisabled } from '../../ducks/UI.duck';
import {
  Page,
  LayoutSingleColumn,
  LayoutWrapperTopbar,
  LayoutWrapperMain,
  LayoutWrapperFooter,
  Footer,
  CTAButton,
} from '../../components';

import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer';
import facebookImage from '../../assets/uupo-share-facebook-1200x630.png';
import activationImage1 from '../../assets/landingRefresh/brand-activation-1.jpg';
import activationImage2 from '../../assets/landingRefresh/brand-activation-2.jpg';
import activationImage3 from '../../assets/landingRefresh/brand-activation-3.jpg';
// TODO: replace with the real "red awning storefront" FAQ image once provided.
import faqImage from '../../assets/landingRefresh/cities-hero-storefront.jpg';

import css from './LandingUupo.module.css';

// The design uses Montserrat 600/700/800; the app only loads weight 500
// globally, so load the bold weights for this landing to render titles crisply.
const fontCss = `@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;600;700;800&display=swap');`;

export const LandingUupoComponent = props => {
  const { intl, scrollingDisabled } = props;

  // Schema for search engines (helps them to understand what this page is about)
  const siteTitle = config.siteTitle;
  const schemaTitle = intl.formatMessage({ id: 'LandingUupo.schemaTitle' }, { siteTitle });
  const schemaDescription = intl.formatMessage({ id: 'LandingUupo.schemaDescription' });
  const schemaImage = `${config.canonicalRootURL}${facebookImage}`;
  const heroVideoUrl = intl.formatMessage({ id: 'SectionHeroUupo.videoLink' });

  const activationCards = [
    {
      image: activationImage1,
      labelId: 'LandingBrands.card1Label',
      subId: 'LandingBrands.card1Sub',
      urgency: false,
    },
    {
      image: activationImage2,
      labelId: 'LandingBrands.card2Label',
      subId: 'LandingBrands.card2Sub',
      urgency: false,
    },
    {
      image: activationImage3,
      labelId: 'LandingBrands.card3Label',
      subId: 'LandingBrands.card3Sub',
      urgency: true,
    },
  ];

  return (
    <Page
      className={css.root}
      scrollingDisabled={scrollingDisabled}
      contentType="website"
      description={schemaDescription}
      title={schemaTitle}
      primaryKeywords={intl.formatMessage({ id: 'LandingUupo.keywords' })}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'WebPage',
        description: schemaDescription,
        name: schemaTitle,
        image: [schemaImage],
      }}
    >
      <style dangerouslySetInnerHTML={{ __html: fontCss }} />
      <LayoutSingleColumn>
        <LayoutWrapperTopbar>
          <TopbarContainer />
        </LayoutWrapperTopbar>

        <LayoutWrapperMain className={css.mainWrapper}>
          {/* ===== HERO ===== */}
          <section className={css.brandHero}>
            <div className={css.brandHeroText}>
              <h1 className={css.brandHeroTitle}>
                <FormattedMessage id="LandingBrands.heroTitle" />
              </h1>
              <p className={css.brandHeroTagline}>
                <FormattedMessage id="LandingBrands.heroTagline" />
              </p>
              <p className={css.brandHeroSub}>
                <FormattedMessage id="LandingBrands.heroSub" />
              </p>
              <CTAButton name="SearchPage" variant="secondary">
                <FormattedMessage id="LandingBrands.heroButton" />
              </CTAButton>
            </div>
            <div className={css.brandHeroMedia}>
              <div className={css.brandHeroVideo}>
                <iframe
                  key={heroVideoUrl}
                  src={heroVideoUrl}
                  title="Meet ÛUPO"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                />
              </div>
            </div>
          </section>

          {/* ===== ACTIVACIONES ===== */}
          <section className={css.activSection}>
            <div className={css.activDivider}>
              <span className={css.activTri} />
            </div>
            <div className={css.activTitleWrap}>
              <h2 className={css.activTitle}>
                <FormattedMessage id="LandingBrands.activTitle" />
              </h2>
            </div>
            <div className={css.photoGrid}>
              {activationCards.map((card, index) => (
                <div
                  key={index}
                  className={
                    card.urgency ? `${css.photoCard} ${css.photoCardUrgency}` : css.photoCard
                  }
                >
                  <img className={css.photoImg} src={card.image} alt="" />
                  <div className={css.photoOverlay}>
                    <div className={css.photoLabel}>
                      <FormattedMessage id={card.labelId} />
                    </div>
                    <div className={css.photoSub}>
                      <FormattedMessage id={card.subId} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className={css.activCtaWrap}>
              <CTAButton name="SearchPage" variant="primary">
                <FormattedMessage id="LandingBrands.activButton" />
              </CTAButton>
            </div>
          </section>

          {/* ===== FAQ ===== */}
          <section className={css.faqSection}>
            <div className={css.faqLeft}>
              <h2 className={css.faqTitle}>
                <FormattedMessage id="LandingBrands.faqTitle" />
              </h2>
              <p className={css.faqText}>
                <FormattedMessage id="LandingBrands.faqText" />
              </p>
              <CTAButton name="FaqPage" variant="primary">
                <FormattedMessage id="LandingBrands.faqButton" />
              </CTAButton>
            </div>
            <div className={css.faqRight}>
              <img className={css.faqImg} src={faqImage} alt="" />
            </div>
          </section>
        </LayoutWrapperMain>

        <LayoutWrapperFooter>
          <Footer />
        </LayoutWrapperFooter>
      </LayoutSingleColumn>
    </Page>
  );
};

const { bool, object } = PropTypes;

LandingUupoComponent.propTypes = {
  scrollingDisabled: bool.isRequired,

  // from withRouter
  history: object.isRequired,
  location: object.isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
};

const mapStateToProps = state => {
  const { isAuthenticated } = state.Auth;
  const { latestListingsIds } = state.LandingPage;
  const latestListings = getListingsById(state, latestListingsIds);
  return {
    latestListings: latestListings,
    isAuthenticated,
    scrollingDisabled: isScrollingDisabled(state),
  };
};

const LandingUupo = compose(withRouter, connect(mapStateToProps), injectIntl)(LandingUupoComponent);

export default LandingUupo;
